import request from './request'
import type { CareScheduleAssignTargetType } from './CareScheduleAssign'
import {
  readDayStatusByDateFromSchedulePayload,
  type CareScheduleDayAvailabilityTone,
} from './CareScheduleAvailability'
import { classifyCareSidebarSubBucket } from './hmCareOrderTypes'
import {
  careOrderListItemToCardDto,
  extractCareOrderListArray,
  type CareOrderListItem,
  type CareOrderMetaData,
  type OrderCardDTO,
} from './orders'
import type { TechnicianListItem } from './technicianslist'

/** context `unscheduled_inspections` 中 follow-up 行常被标成主单 `pending_*`，需归一为 FU 状态 */
export function normalizeFollowUpInspectionStatus(status: string): string {
  const raw = status.trim()
  if (!raw) return raw
  const bucket = classifyCareSidebarSubBucket(raw)
  if (bucket === 'fu_pend_sched' || bucket === 'fu_pend_resched') return raw
  if (bucket === 'pend_sched') return 'fu_pending_schedule'
  if (bucket === 'pend_resched') return 'fu_pending_reschedule'
  const k = raw.toLowerCase().replace(/[\s-]+/g, '_')
  if (k === 'pending_schedule') return 'fu_pending_schedule'
  if (k === 'pending_reschedule') return 'fu_pending_reschedule'
  return raw
}

/** POST `/api/admin/care-schedule/context` 请求体（raw-json） */
export interface CareScheduleContextRequest {
  target_type: CareScheduleAssignTargetType
  /** 主单为订单主键 `id`；Follow-up 为 follow-up 主键 */
  target_id: number
  /** 当前周起始日（周日）`YYYY-MM-DD`；现传选中/查询日 YYYY-MM-DD */
  week_start: string
}

export interface CareScheduleContextResponse {
  code: number
  msg: string
  data?: unknown
}

/** context 接口 `current_schedule`：当前改期目标的已确认预约 */
export interface CareScheduleCurrentSchedule {
  technicianId?: number
  date?: string
  timeStart?: string
  timeEnd?: string
  timeText?: string
}

export interface ParsedCareScheduleContext {
  weekStart?: string
  /** context `week_dates` / `day_status` 解析出的日期条颜色 */
  dayStatusByDate: Record<string, CareScheduleDayAvailabilityTone>
  technicians: TechnicianListItem[]
  pendingOrders: OrderCardDTO[]
  assignedOrders: OrderCardDTO[]
  /** 左侧 Unscheduled Inspections 列表（context 权威数据源） */
  unscheduledInspections: OrderCardDTO[]
  currentSchedule?: CareScheduleCurrentSchedule
  meta?: CareOrderMetaData
}

const ORDER_LIST_KEYS = [
  'pending_orders',
  'pending_list',
  'unscheduled_orders',
  'pool_orders',
  'pending_schedule_orders',
  'schedule_pool',
] as const

const ASSIGNED_LIST_KEYS = [
  'assigned_orders',
  'assigned_list',
  'scheduled_orders',
  'booked_orders',
] as const

const TECHNICIAN_KEYS = ['technicians', 'inspectors', 'technician_list', 'inspector_list'] as const

const UNSCHEDULED_INSPECTIONS_KEYS = ['unscheduled_inspections', 'unscheduledInspections'] as const

function readOrderCardsFromData(data: Record<string, unknown>, keys: readonly string[]): OrderCardDTO[] {
  for (const key of keys) {
    const v = data[key]
    if (Array.isArray(v)) {
      return v
        .filter((x) => x != null && typeof x === 'object')
        .map((x) => careOrderListItemToCardDto(x as CareOrderListItem))
    }
    if (v != null && typeof v === 'object') {
      const nested = extractCareOrderListArray(v as Record<string, unknown>)
      if (nested?.length) {
        return nested.map((x) => careOrderListItemToCardDto(x as CareOrderListItem))
      }
    }
  }
  return []
}

function readContextStringField(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = row[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

function readContextNumericId(raw: unknown): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.trunc(raw)
  const s = String(raw ?? '').trim()
  if (/^\d+$/.test(s)) {
    const n = Number(s)
    if (Number.isFinite(n) && n > 0) return Math.trunc(n)
  }
  return undefined
}

/** context `unscheduled_inspections[]` 精简行 → list 行（补全 address / target_type 等） */
function contextInspectionRowToListItem(row: Record<string, unknown>): CareOrderListItem {
  const targetType = readContextStringField(row, 'target_type', 'targetType').toLowerCase() || 'order'
  const orderId = readContextStringField(row, 'order_id', 'orderId')
  const followUpId = readContextStringField(row, 'follow_up_id', 'followUpId')
  const address = readContextStringField(
    row,
    'address',
    'service_address',
    'serviceAddress',
    'short_address',
    'shortAddress',
  )
  const serviceName = readContextStringField(row, 'service_name', 'serviceName')
  const homeId = readContextStringField(row, 'home_id', 'homeId')
  const rawStatus = readContextStringField(row, 'status', 'service_status', 'serviceStatus')
  const status =
    targetType === 'followup' ? normalizeFollowUpInspectionStatus(rawStatus) : rawStatus
  const targetId = readContextNumericId(row.target_id ?? row.targetId ?? row.id)
  /** follow-up 行：主单主键用 order_id；跟进行主键用 target_id */
  const mainOrderPk =
    targetType === 'followup' ? readContextNumericId(orderId) : targetId

  const base: CareOrderListItem = {
    id: mainOrderPk ?? 0,
    order_id: orderId,
    home_id: homeId,
    service_name:
      targetType === 'followup'
        ? serviceName && serviceName.trim().toLowerCase() !== 'follow-up care'
          ? serviceName
          : 'Care'
        : serviceName || 'Care',
    service_address: address || undefined,
    service_status: status || undefined,
  }

  if (targetType === 'followup') {
    return {
      ...base,
      followups: [
        {
          id: targetId,
          follow_up_id: followUpId || orderId,
          service_address: address || undefined,
          status: status || undefined,
          status_text: status || undefined,
        },
      ],
    }
  }

  return base
}

function readUnscheduledInspectionsFromData(data: Record<string, unknown>): OrderCardDTO[] {
  for (const key of UNSCHEDULED_INSPECTIONS_KEYS) {
    const v = data[key]
    if (Array.isArray(v)) {
      return v
        .filter((x) => x != null && typeof x === 'object')
        .map((x) =>
          careOrderListItemToCardDto(
            contextInspectionRowToListItem(x as Record<string, unknown>),
          ),
        )
    }
    if (v != null && typeof v === 'object') {
      const nested = extractCareOrderListArray(v as Record<string, unknown>)
      if (nested?.length) {
        return nested
          .filter((x) => x != null && typeof x === 'object')
          .map((x) =>
            careOrderListItemToCardDto(
              contextInspectionRowToListItem(x as Record<string, unknown>),
            ),
          )
      }
    }
  }
  return []
}

function readTechniciansFromData(data: Record<string, unknown>): TechnicianListItem[] {
  for (const key of TECHNICIAN_KEYS) {
    const v = data[key]
    if (Array.isArray(v)) return v as TechnicianListItem[]
    if (v != null && typeof v === 'object') {
      const nested = (v as Record<string, unknown>).list
      if (Array.isArray(nested)) return nested as TechnicianListItem[]
    }
  }
  return []
}

function readMetaFromData(data: Record<string, unknown>): CareOrderMetaData | undefined {
  const meta = data.meta ?? data.care_meta ?? data.order_meta
  if (meta != null && typeof meta === 'object') return meta as CareOrderMetaData
  return undefined
}

function readCurrentScheduleFromData(
  data: Record<string, unknown>,
): CareScheduleCurrentSchedule | undefined {
  const raw = data.current_schedule ?? data.currentSchedule
  if (raw == null || typeof raw !== 'object') return undefined
  const o = raw as Record<string, unknown>

  const technicianRaw = o.technician_id ?? o.technicianId
  const technicianId =
    typeof technicianRaw === 'number'
      ? technicianRaw
      : typeof technicianRaw === 'string'
        ? Number(technicianRaw.trim())
        : NaN

  const date =
    typeof o.date === 'string'
      ? o.date.trim()
      : typeof o.service_date === 'string'
        ? o.service_date.trim()
        : undefined
  const timeStart =
    typeof o.time_start === 'string'
      ? o.time_start.trim()
      : typeof o.timeStart === 'string'
        ? o.timeStart.trim()
        : undefined
  const timeEnd =
    typeof o.time_end === 'string'
      ? o.time_end.trim()
      : typeof o.timeEnd === 'string'
        ? o.timeEnd.trim()
        : undefined
  const timeText =
    typeof o.time_text === 'string'
      ? o.time_text.trim()
      : typeof o.timeText === 'string'
        ? o.timeText.trim()
        : undefined

  if (!date && !timeStart && !timeEnd && !timeText) return undefined

  return {
    technicianId:
      Number.isFinite(technicianId) && technicianId > 0 ? Math.trunc(technicianId) : undefined,
    date: date || undefined,
    timeStart: timeStart || undefined,
    timeEnd: timeEnd || undefined,
    timeText: timeText || undefined,
  }
}

export function currentScheduleFromContextTarget(
  raw: unknown,
): CareScheduleCurrentSchedule | undefined {
  if (raw == null || typeof raw !== 'object') return undefined
  const row = raw as Record<string, unknown>
  return mapInspectorOrderRowToCurrentSchedule(row, row.technician_id ?? row.technicianId)
}

function mapInspectorOrderRowToCurrentSchedule(
  row: Record<string, unknown>,
  inspectorId: unknown,
): CareScheduleCurrentSchedule | undefined {
  const date =
    typeof row.date === 'string'
      ? row.date.trim()
      : typeof row.service_date === 'string'
        ? row.service_date.trim()
        : undefined
  const timeStart =
    typeof row.time_start === 'string'
      ? row.time_start.trim()
      : typeof row.timeStart === 'string'
        ? row.timeStart.trim()
        : undefined
  const timeEnd =
    typeof row.time_end === 'string'
      ? row.time_end.trim()
      : typeof row.timeEnd === 'string'
        ? row.timeEnd.trim()
        : undefined
  const timeText =
    typeof row.time_text === 'string'
      ? row.time_text.trim()
      : typeof row.timeText === 'string'
        ? row.timeText.trim()
        : undefined
  if (!date && !timeStart && !timeEnd && !timeText) return undefined

  const technicianRaw = inspectorId
  const technicianId =
    typeof technicianRaw === 'number'
      ? technicianRaw
      : typeof technicianRaw === 'string'
        ? Number(technicianRaw.trim())
        : NaN

  return {
    technicianId:
      Number.isFinite(technicianId) && technicianId > 0 ? Math.trunc(technicianId) : undefined,
    date: date || undefined,
    timeStart: timeStart || undefined,
    timeEnd: timeEnd || undefined,
    timeText: timeText || undefined,
  }
}

/**
 * 无根级 `current_schedule` 时，从 `inspectors[].orders[]` 取当前改期目标那一行。
 * 跟进行必须匹配 `target_type: followup`，避免误用同单主单 `order` 行（如 10:00–14:00）。
 */
export function deriveCurrentScheduleFromInspectorOrders(
  data: Record<string, unknown>,
  opts: {
    targetType: 'order' | 'followup'
    orderId: string
    followUpId?: string
    followUpPrimaryId?: number
  },
): CareScheduleCurrentSchedule | undefined {
  const orderId = opts.orderId.trim()
  if (!orderId) return undefined

  const inspectors = data.inspectors ?? data.technicians
  if (!Array.isArray(inspectors)) return undefined

  for (const ins of inspectors) {
    if (ins == null || typeof ins !== 'object') continue
    const insObj = ins as Record<string, unknown>
    const orders = insObj.orders
    if (!Array.isArray(orders)) continue

    for (const ord of orders) {
      if (ord == null || typeof ord !== 'object') continue
      const row = ord as Record<string, unknown>
      const rowOrderId = String(row.order_id ?? row.orderId ?? '').trim()
      if (rowOrderId && rowOrderId !== orderId) continue

      const tt = String(row.target_type ?? row.targetType ?? '')
        .trim()
        .toLowerCase()

      if (opts.targetType === 'followup') {
        if (tt !== 'followup') continue
        const fuId = String(row.follow_up_id ?? row.followUpId ?? '').trim()
        const rowPkRaw = row.id ?? row.follow_up_pk
        const rowPk =
          typeof rowPkRaw === 'number' && Number.isFinite(rowPkRaw)
            ? Math.trunc(rowPkRaw)
            : typeof rowPkRaw === 'string'
              ? Number(rowPkRaw.trim())
              : NaN
        const hasFuFilter = Boolean(opts.followUpId?.trim()) || opts.followUpPrimaryId != null
        if (hasFuFilter) {
          const idMatch = Boolean(opts.followUpId?.trim() && fuId && fuId === opts.followUpId!.trim())
          const pkMatch =
            opts.followUpPrimaryId != null &&
            Number.isFinite(rowPk) &&
            Math.trunc(rowPk) === Math.trunc(opts.followUpPrimaryId)
          if (!idMatch && !pkMatch) continue
        }
      } else if (tt === 'followup') {
        continue
      }

      const mapped = mapInspectorOrderRowToCurrentSchedule(row, insObj.id)
      if (mapped) return mapped
    }
  }

  return undefined
}

/** context `inspectors[].orders[]` 是否在指定日有任务（用于 week-date-card 蓝色回显） */
export function contextDayHasOrdersOnDate(
  data: Record<string, unknown>,
  dayYmd: string,
  technicianId?: string,
): boolean {
  const busy = extractBusyInspectorIdsForDate(data, dayYmd)
  if (!technicianId?.trim()) return busy.length > 0
  const want = technicianId.trim()
  return busy.some((id) => id === want || String(Number(id)) === String(Number(want)))
}

/** 指定日有任务的检查员 id 列表（来自 context inspectors） */
export function extractBusyInspectorIdsForDate(
  data: Record<string, unknown>,
  dayYmd: string,
): string[] {
  const ymd = dayYmd.trim()
  if (!ymd) return []
  const inspectors = data.inspectors ?? data.technicians
  if (!Array.isArray(inspectors)) return []
  const ids: string[] = []
  for (const ins of inspectors) {
    if (ins == null || typeof ins !== 'object') continue
    const insObj = ins as Record<string, unknown>
    const techId = String(insObj.id ?? insObj.technician_id ?? '').trim()
    if (!techId) continue
    const orders = insObj.orders
    if (!Array.isArray(orders)) continue
    const hasOnDay = orders.some((ord) => {
      if (ord == null || typeof ord !== 'object') return false
      const row = ord as Record<string, unknown>
      const dateRaw = row.date ?? row.service_date ?? row.serviceDate
      if (typeof dateRaw !== 'string') return false
      const m = dateRaw.trim().match(/^(\d{4}-\d{2}-\d{2})/)
      return m?.[1] === ymd
    })
    if (hasOnDay) ids.push(techId)
  }
  return ids
}

/** 解析 context 接口 `data` 为排程弹窗可用结构 */
export function parseCareScheduleContextResponse(raw: unknown): ParsedCareScheduleContext | null {
  if (raw == null || typeof raw !== 'object') return null
  const r = raw as CareScheduleContextResponse
  if (r.code !== 200 || r.data == null || typeof r.data !== 'object') return null
  const data = r.data as Record<string, unknown>

  const pendingOrders = readOrderCardsFromData(data, ORDER_LIST_KEYS)
  const assignedOrders = readOrderCardsFromData(data, ASSIGNED_LIST_KEYS)
  const unscheduledInspections = readUnscheduledInspectionsFromData(data)
  const technicians = readTechniciansFromData(data)
  const weekStart =
    typeof data.week_start === 'string'
      ? data.week_start.trim()
      : typeof data.weekStart === 'string'
        ? data.weekStart.trim()
        : undefined
  const dayStatusByDate = readDayStatusByDateFromSchedulePayload(data)

  return {
    weekStart: weekStart || undefined,
    dayStatusByDate,
    technicians,
    pendingOrders,
    assignedOrders,
    unscheduledInspections,
    currentSchedule: readCurrentScheduleFromData(data),
    meta: readMetaFromData(data),
  }
}

/**
 * 获取排期弹窗上下文（检查员、待排期池、已排期占用等）。
 * Header `token` 由 `request` 拦截器自动注入。
 */
export async function postCareScheduleContext(
  body: CareScheduleContextRequest,
): Promise<ParsedCareScheduleContext> {
  const raw = await request.post<CareScheduleContextResponse>(
    '/api/admin/care-schedule/context',
    body,
  )
  const parsed = parseCareScheduleContextResponse(raw)
  if (parsed) return parsed
  const msg =
    typeof raw?.msg === 'string' && raw.msg.trim() ? raw.msg : 'Failed to load schedule context'
  throw new Error(msg)
}
